import { useEffect, useState } from 'react'

const STORAGE_KEY = 'site_lang'
// 语言选择来源页面：global（境外渠道页）/ official（官网），境外页面据此判断是否沿用存储语言
const STORAGE_SOURCE_KEY = 'site_lang_source'
const DEFAULT_LANG = 'zh'
const SUPPORTED_LANGUAGES = ['zh', 'en', 'fr', 'de']

function isGlobalPage() {
  if (typeof window === 'undefined') return false
  return window.location.pathname.startsWith('/global')
}

function detectBrowserLanguage() {
  if (typeof window === 'undefined') return DEFAULT_LANG
  const languageCandidates = [
    ...(Array.isArray(window.navigator.languages) ? window.navigator.languages : []),
    window.navigator.language,
    window.navigator.userLanguage
  ].filter(Boolean)

  for (const language of languageCandidates) {
    const lower = String(language).toLowerCase()
    if (lower.startsWith('zh')) return 'zh'
    if (lower.startsWith('fr')) return 'fr'
    if (lower.startsWith('de')) return 'de'
    if (lower.startsWith('en')) return 'en'
  }

  return 'en'
}

function normalizeLang(value) {
  // en-US 区域码归一为 en（用户确认 2026-09-09，仅支持 en-US 归一）
  if (value === 'en-US' || value === 'en_US' || value === 'en-us' || value === 'en_us') return 'en'
  return SUPPORTED_LANGUAGES.includes(value) ? value : DEFAULT_LANG
}

export function getStoredSiteLanguage() {
  if (typeof window === 'undefined') return DEFAULT_LANG
  const stored = window.localStorage.getItem(STORAGE_KEY)
  // 境外渠道页面默认英文优先：仅当语言是在境外页面上手动选择时才沿用（用户决策 2026-09-10）
  if (isGlobalPage()) {
    if (stored && window.localStorage.getItem(STORAGE_SOURCE_KEY) === 'global') {
      return normalizeLang(stored)
    }
    return 'en'
  }
  if (stored) return normalizeLang(stored)
  return detectBrowserLanguage()
}

export function setStoredSiteLanguage(lang) {
  if (typeof window === 'undefined') return
  const nextLang = normalizeLang(lang)
  window.localStorage.setItem(STORAGE_KEY, nextLang)
  window.localStorage.setItem(STORAGE_SOURCE_KEY, isGlobalPage() ? 'global' : 'official')
  window.dispatchEvent(new CustomEvent('site-language-change', { detail: nextLang }))
}

export default function useSiteLanguage() {
  const [lang, setLangState] = useState(getStoredSiteLanguage())

  useEffect(() => {
    const syncLanguage = (event) => {
      const nextLang = event?.detail || getStoredSiteLanguage()
      setLangState(normalizeLang(nextLang))
    }

    window.addEventListener('storage', syncLanguage)
    window.addEventListener('site-language-change', syncLanguage)

    return () => {
      window.removeEventListener('storage', syncLanguage)
      window.removeEventListener('site-language-change', syncLanguage)
    }
  }, [])

  const setLang = (nextLang) => {
    const normalized = normalizeLang(nextLang)
    setLangState(normalized)
    setStoredSiteLanguage(normalized)
  }

  return { lang, setLang }
}
